import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import {
  SaveIcon,
  BackIcon,
  EditIcon,
  ViewIcon,
  DeleteIcon,
  GoodIcon,
  BadIcon,
  GenericAdd,
} from "../../components/common/NucleusSymbolsAndIcons";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSMCreateInvMovement,
  useTSMCreateInvMovementBatch,
  useTSMUpdateInvMovement,
} from "../../hooks/useTSInvMovement";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  InvMovCreate,
  InvMovBatchCreate,
  INV_MOV_TYPES,
  InvMovementType,
} from "../../types/invMovementTypes";
import {
  TypedSelectOption,
  LotOption,
  ItemOption,
  UnitOption,
  LocationOption,
  EmployeeOption,
  VendorOption,
  InventoryStatusOption,
  InventoryPositionOption,
  YupTypedSelectOption,
} from "../../types/common/selectOptions";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import {
  NumericEntryField,
  NumericDisplayField,
} from "../../components/common/CustomFields";
import stringUtils from "../../utils/stringUtils";
import { DynamicRequestFilter } from "../../types/dynamicQueryTypes";

type ALotOption = LotOption<{
  itemId: string;
  recUomId: string;
  initRev: string;
  relRev: string | null;
  expDate: string | null;
}>;

type AItemOption = ItemOption<{
  uomTypeId: string;
  storeId: string;
  prefBin: string;
  expires: boolean;
}>;

type AUnitOption = UnitOption<{
  uomTypeId: string;
  conv: number;
}>;

type APosOption = InventoryPositionOption<{
  lotId: string;
  lotNum: string;
  lotRecUnitId: string;
  lotRecUnitSymbol: string;
  lotRecUnitConv: number;
  lotRelRev: string;
  lotInitRev: string;
  lotExpDate: string;
  lotRFT: boolean;
  lotPD: boolean;
  lotRWRK: boolean;
  itemId: string;
  itemNum: string;
  itemTypeId: string;
  itemPrefBin: string;
  itemExpires: boolean;
  locId: string;
  locName: string;
  statId: string;
  statName: string;
  quant: number;
}>;

type ALocOption = LocationOption<{}>;

type AInvStatOption = InventoryStatusOption<{
  exclude: boolean;
  alloc: boolean;
  pick: boolean;
}>;

type AMovTypeOption = TypedSelectOption<"movtype", {}, InvMovementType>;
const [movTypeOpts, defaultMovType] = (() => {
  const opts: AMovTypeOption[] = INV_MOV_TYPES.filter((val) =>
    ["ADJUSTMENT", "INSPECT", "RELEASE", "TRANSFER"].includes(val),
  ).flatMap((val, ind) => {
    if (val === "RELEASE") {
      return [
        {
          label: `${stringUtils.capFirstLetter(
            val.replace(/[_]/g, " "),
          )} - Built`,
          type: "movtype",
          value: { id: val as InvMovementType },
        },
        {
          label: `${stringUtils.capFirstLetter(
            val.replace(/[_]/g, " "),
          )} - Received`,
          type: "movtype",
          value: { id: val as InvMovementType },
        },
      ];
    } else if (val === "INSPECT") {
      return [
        {
          label: "Receiving Inspection",
          type: "movtype",
          value: { id: val },
        },
      ];
    } else {
      return [
        {
          label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
          type: "movtype",
          value: { id: val },
        },
      ];
    }
  });

  return [opts.sort((a, b) => a.label.localeCompare(b.label)), opts[0]];
})();
type ARecTableOption = TypedSelectOption<"table", {}, string>;
type ARecordOption = TypedSelectOption<"record", {}>;
type ANonConOption = TypedSelectOption<"noncon", {}, string>;

interface newMovementRequest {
  fromPos: APosOption | null;
  toLoc: ALocOption | null;
  toStat: AInvStatOption | null;
  lot: ALotOption | null;
  item: AItemOption | null;
  uom: AUnitOption | null;
  quantity: number;
  releaseRev: string;
  expirationDate: string | null;
  notes: string | null;
  rft: boolean;
  rw: boolean;
  pd: boolean;
  ncs: ANonConOption[];
}

interface InvMovFormData {
  movement_type: AMovTypeOption;
  associated_table: ARecTableOption | null;
  associated_id: ARecordOption | null;
  notes: string | null;
  movements: newMovementRequest[];
}
const defaultValues: InvMovFormData = {
  movement_type: defaultMovType,
  associated_table: null,
  associated_id: null,
  notes: null,
  movements: [],
};

const movSchema = yup.object({
  fromPos: YupTypedSelectOption.nullable().test({
    name: "release-from",
    test: (value, context) => {
      console.log("Release From Yup Test Context", context);
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({
          message: "fromPos||Missing Movement Type",
        });
      }
      if (
        movType.label.includes("Release") ||
        movType.label === "Receiving Inspection" ||
        movType.label === "Transfer"
      ) {
        if (!value) {
          return context.createError({
            message:
              "fromPos||Origin Position is Required for Receiving Inspection, Release - Built, Release - Received, and Transfer movements.",
          });
        }
      } else if (movType.label === "Adjustment") {
        if (context.options.context?.isTo === false) {
          if (!value) {
            return context.createError({
              message:
                "fromPos||Origin Position is Required Remove Adjustment movements.",
            });
          }
        }
      }
      return true;
    },
  }),
  toLoc: YupTypedSelectOption.nullable().test({
    name: "toloc",
    test: (value, context) => {
      console.log("To Loc Yup Test Context", context);
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({ message: "Missing Movement Type" });
      }
      if (
        movType.label.includes("Release") ||
        movType.label === "Receiving Inspection" ||
        movType.label === "Transfer"
      ) {
        return !!value;
      } else if (movType.label === "Adjustment") {
        if (context.options.context?.isTo) {
          return !!value;
        }
      }
      return true;
    },
    message:
      "toLoc||To Location is Required for Receiving Inspection, Release - Built, Release - Received, Transfer, and Add Adjustment movements.",
  }),
  toStat: YupTypedSelectOption.nullable().test({
    name: "toStat",
    test: (value, context) => {
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({ message: "Missing Movement Type" });
      }
      if (
        movType.label.includes("Release") ||
        movType.label === "Receiving Inspection" ||
        movType.label === "Transfer"
      ) {
        return !!value;
      } else if (movType.label === "Adjustment") {
        if (context.options.context?.isTo) {
          return !!value;
        }
      }
      return true;
    },
    message:
      "toStat||To Status is Required for Receiving Inspection, Release - Built, Release - Received, Transfer, and Add Adjustment movements.",
  }),
  lot: YupTypedSelectOption.nullable().test({
    name: "lot",
    test: (value, context) => {
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({ message: "lot||Missing Movement Type" });
      }
      if (movType.label === "Adjustment" || movType.label === "Transfer") {
        return !!value;
      }
      return true;
    },
    message: "lot||Lot is Required for all Adjustment and Transfer movements.",
  }),
  item: YupTypedSelectOption.nullable().test({
    name: "item",
    test: (value, context) => {
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({ message: "item||Missing Movement Type" });
      }
      if (movType.label === "Adjustment" || movType.label === "Transfer") {
        return !!value;
      }
      return true;
    },
    message:
      "item||Item is Required for all Adjustment and Transfer movements.",
  }),
  uom: YupTypedSelectOption.required("uom||UOM is Required"),
  quantity: yup.number().test({
    name: "quantity-check",
    test: (value, context) => {
      const uomOpt: AUnitOption | null = context.parent.uom || null;
      if (!uomOpt) {
        return context.createError({
          message: "quantity||UOM is Required to perform Quantity Validation",
        });
      }
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({
          message: "quantity||Missing Movement Type",
        });
      }
      if (!value) {
        return context.createError({
          message: "quantity||Quantity is Required",
        });
      }
      if (value === 0) {
        return context.createError({
          message: "quantity||Quantity must be greater than 0",
        });
      }
      if (
        (movType.label.includes("Release") ||
          movType.label === "Receiving Inspection" ||
          movType.label === "Transfer" ||
          (movType.label === "Adjustment" &&
            context.options.context?.isTo === false)) &&
        parseFloat((value * uomOpt.value.conv).toFixed(4)) >
          (context.options.context?.availQuant || 0)
      ) {
        console.log(
          "Mov Value",
          parseFloat((value * uomOpt.value.conv).toFixed(4)),
        );
        console.log("Avail", context.options.context?.availQuant || 0);
        return context.createError({
          message: `quantity||Cannot transfer more than is available. Available Quantity: ${
            context.options.context?.availQuant || 0
          }x ${uomOpt.label}`,
        });
      }
      return true;
    },
  }),
  releaseRev: yup.string().test({
    name: "releaseRev",
    test: (value, context) => {
      const movType: AMovTypeOption | null =
        context.options.context?.movType || null;
      if (movType === null) {
        return context.createError({
          message: "releaseRev||Missing Movement Type",
        });
      }
      if (movType.label.includes("Release")) {
        return !!value;
      }
      return true;
    },
    message:
      "releaseRev||Released Revision is Required for Release - Built and Release - Received movements",
  }),
  notes: yup.string().optional().nullable(),
  rft: yup.boolean(),
  rw: yup.boolean(),
  pd: yup.boolean(),
  ncs: yup.array().of(YupTypedSelectOption.required()),
});

// Validation schema
const fullFormSchema = yup.object({
  movement_type: YupTypedSelectOption.required(),
  associated_table: YupTypedSelectOption.optional().nullable(),
  associated_id: YupTypedSelectOption.optional().nullable(),
  notes: yup.string().optional().nullable(),
  movements: yup
    .array()
    .of(yup.object({}))
    .min(1, "At least one movement must be present."),
});

const MovementRow: React.FC<{
  index: number;
  formMode: string;
  xferMode: AMovTypeOption;
  remove: (index: number) => void;
  lineData: newMovementRequest & { id: string };
  handleDialogOpen: (index: number, lineData: newMovementRequest) => void;
}> = ({ index, formMode, xferMode, remove, lineData, handleDialogOpen }) => {
  return (
    <TableRow>
      <TableCell sx={{ textAlign: "center" }}>{`#${index + 1}`}</TableCell>
      <TableCell sx={{ textAlign: "center" }}>
        <Typography variant="body1">
          {xferMode.label.includes("Release") ||
          xferMode.label.includes("Inspect")
            ? `${lineData.fromPos?.value.itemNum} Lot: ${lineData.fromPos?.value.lotNum}`
            : `${lineData.item?.label} Lot: ${lineData.lot?.label}`}
        </Typography>
      </TableCell>
      <TableCell sx={{ textAlign: "center" }}>
        <Typography variant="body1">
          {lineData.fromPos
            ? `${lineData.fromPos?.value.locName} (${lineData.fromPos?.value.statName})`
            : "No Origin Position Data"}
        </Typography>
      </TableCell>
      <TableCell sx={{ textAlign: "center" }}>
        <Typography variant="body1">
          {lineData.toLoc && lineData.toStat
            ? `${lineData.toLoc.label} (${lineData.toStat.label})`
            : "No Destination Position Data"}
        </Typography>
      </TableCell>
      <TableCell sx={{ textAlign: "center" }}>{`${lineData.quantity}x ${
        lineData.uom?.label || "Unknown UOM"
      }`}</TableCell>
      <TableCell sx={{ textAlign: "center" }}>
        <Typography variant="body1">{lineData.notes}</Typography>
      </TableCell>
      <TableCell>
        <Box display="flex" gap={1}>
          {formMode === "create" && (
            <Button
              variant="contained"
              color="info"
              onClick={() => handleDialogOpen(index, lineData)}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          {formMode === "create" && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => remove(index)}
              startIcon={<DeleteIcon />}
            >
              Remove
            </Button>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const MovementDialog: React.FC<{
  open: boolean;
  initData: newMovementRequest | null;
  onDialogClose: () => void;
  onDialogSubmit: (data: newMovementRequest) => void;
  mode: AMovTypeOption;
  superUser: boolean;
}> = ({ open, initData, onDialogClose, onDialogSubmit, mode, superUser }) => {
  const [isTo, setIsTo] = useState<boolean>(true);

  const [currentNcOpt, setCurrentNcOpt] = useState<ANonConOption | null>(null);
  const [newNcOpts, setNewNcOpts] = useState<ANonConOption[]>([]);
  const [newNcNum, setNewNcNum] = useState<string>("");

  const [thisMovementData, setThisMovementData] = useState<newMovementRequest>({
    fromPos: null,
    toLoc: null,
    toStat: null,
    lot: null,
    item: null,
    uom: null,
    releaseRev: "",
    expirationDate: null,
    quantity: 0,
    notes: null,
    rft: false,
    rw: false,
    pd: false,
    ncs: [],
  });

  const [dialogErrors, setDialogErrors] = useState<{
    general: string[];
    fromPos: string | null;
    toLoc: string | null;
    toStat: string | null;
    lot: string | null;
    item: string | null;
    uom: string | null;
    releaseRev: string | null;
    quantity: string | null;
    notes: string | null;
    rft: string | null;
    rw: string | null;
    pd: string | null;
    ncs: string | null;
    newNcNum: string | null;
  }>({
    general: [],
    fromPos: null,
    toLoc: null,
    toStat: null,
    lot: null,
    item: null,
    uom: null,
    releaseRev: null,
    quantity: null,
    notes: null,
    rft: null,
    rw: null,
    pd: null,
    ncs: null,
    newNcNum: null,
  });

  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "locations",
    alt_name: null,
    primary_fields: ["id", "name"],
    joins: [],
    filters: [
      [
        {
          table: "locations",
          alt_name: null,
          field: "top_location_name",
          operator: "==",
          value: "Omaha",
          is_not: false,
          inclusive_start: false,
          inclusive_end: false,
          case_insensitive: false,
        },
        {
          table: "locations",
          alt_name: null,
          field: "name",
          operator: "==",
          value: "Omaha",
          is_not: true,
          inclusive_start: false,
          inclusive_end: false,
          case_insensitive: false,
        },
      ],
    ],
    sorts: [
      {
        table: "locations",
        alt_name: null,
        field: "name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const [locOpts, quarLoc] = useMemo(() => {
    if (locsResponse?.items) {
      const opts: ALocOption[] = locsResponse.items.map((loc) => ({
        label: loc["locations.name"],
        type: "location",
        value: {
          id: loc["locations.id"],
        },
      }));
      return [opts, opts.find((o) => o.label === "Quarantine") || null];
    } else {
      return [[], null];
    }
  }, [locsResponse]);

  const {
    data: statusResponse,
    isLoading: statusLoading,
    error: statusError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "inventory_statuses",
    alt_name: null,
    primary_fields: [
      "id",
      "name",
      "exclude_from_mrp",
      "allow_allocation",
      "allow_picking",
    ],
    joins: [],
    filters: [],
    sorts: [
      {
        table: "inventory_statuses",
        alt_name: null,
        field: "name",
        asc: false,
      },
    ],
    subqueries: [],
  });
  const [statOpts, releaseStat, inspectStat, pendReviewStat] = useMemo(() => {
    if (statusResponse?.items) {
      const options: AInvStatOption[] = [];
      statusResponse.items.forEach((val, ind) => {
        const opt: AInvStatOption = {
          label: val["inventory_statuses.name"],
          type: "invstatus",
          value: {
            id: val["inventory_statuses.id"],
            exclude: val["inventory_statuses.exclude_from_mrp"],
            alloc: val["inventory_statuses.allow_allocation"],
            pick: val["inventory_statuses.allow_picking"],
          },
        };
        options.push(opt);
      });
      return [
        options,
        options.find((opt) => opt.label === "Released") || null,
        options.find((opt) => opt.label === "Awaiting Inspection") || null,
        options.find((opt) => opt.label === "Pending Quality Review") || null,
      ];
    } else {
      return [[], null, null, null];
    }
  }, [statusResponse]);

  useEffect(() => {
    if (open) {
      if (initData !== null) {
        setThisMovementData(initData);
      } else {
        setThisMovementData({
          fromPos: null,
          toLoc: mode.label === "Receiving Inspection" ? quarLoc : null,
          toStat:
            mode.label === "Receiving Inspection"
              ? inspectStat
              : mode.label.includes("Release")
                ? releaseStat
                : null,
          lot: null,
          item: null,
          uom: null,
          releaseRev: "",
          expirationDate: null,
          quantity: 0,
          notes: null,
          rft: false,
          rw: false,
          pd: false,
          ncs: [],
        });
      }
      setCurrentNcOpt(null);
      setNewNcNum("");
    }
  }, [open, mode, initData, inspectStat, releaseStat, quarLoc]);

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "items",
      alt_name: null,
      primary_fields: [
        "id",
        "number",
        "uom_type_id",
        "storage_uom_id",
        "preferred_bin_id",
        "item_expires",
      ],
      joins: [],
      filters: [
        [
          {
            table: "items",
            alt_name: null,
            field: "track_lots",
            operator: "==",
            value: true,
            is_not: false,
            inclusive_start: false,
            inclusive_end: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [
        {
          table: "items",
          alt_name: null,
          field: "number",
          asc: true,
        },
      ],
      subqueries: [],
    },
    open && !mode.label.includes("Release") && !mode.label.includes("Inspect"),
  );
  const itemOpts: AItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      return itemsResponse.items.map((itm) => ({
        label: itm["items.number"],
        type: "item",
        value: {
          id: itm["items.id"],
          uomTypeId: itm["items.uom_type_id"],
          storeId: itm["items.storage_uom_id"],
          prefBin: itm["items.preferred_bin_id"],
          expires: itm["items.item_expires"],
        },
      }));
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: lotsResponse,
    isLoading: lotsLoading,
    error: lotsError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "inventory_lots",
      alt_name: null,
      primary_fields: [
        "id",
        "lot_number",
        "item_id",
        "received_uom_unit_id",
        "initial_revision",
        "released_revision",
        "expiration_date",
      ],
      joins: [],
      filters: [
        [
          {
            table: "inventory_lots",
            alt_name: null,
            field: "item_id",
            operator: "==",
            value: thisMovementData.item?.value.id,
            is_not: false,
            inclusive_start: false,
            inclusive_end: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [
        {
          table: "inventory_lots",
          alt_name: null,
          field: "lot_number",
          asc: false,
        },
      ],
      subqueries: [],
    },
    open && !!thisMovementData.item,
  );
  const lotOpts: ALotOption[] = useMemo(() => {
    if (lotsResponse?.items) {
      const opts: ALotOption[] = lotsResponse.items.map((lot) => ({
        label: lot["inventory_lots.lot_number"],
        type: "lot",
        value: {
          id: lot["inventory_lots.id"],
          itemId: lot["inventory_lots.item_id"],
          recUomId: lot["inventory_lots.received_uom_unit_id"],
          initRev: lot["inventory_lots.initial_revision"],
          relRev: lot["inventory_lots.released_revision"] || null,
          expDate: lot["inventory_lots.expiration_date"] || null,
        },
      }));

      return opts;
    } else {
      return [];
    }
  }, [lotsResponse]);

  const {
    data: relInspectPosResponse,
    isLoading: pendReleasePosLoading,
    error: pendReleasePosError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "inventory_positions",
      alt_name: null,
      primary_fields: ["id", "quantity_on_hand"],
      joins: [
        {
          table: "inventory_lots",
          alt_name: null,
          fields: [
            "id",
            "lot_number",
            "initial_revision",
            "released_revision",
            "expiration_date",
            "right_first_time",
            "planned_deviation",
            "rework",
          ],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "inventory_lot_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "uom_units",
          alt_name: null,
          fields: ["id", "unit_symbol", "conversion_factor"],
          condition: {
            remote_table: "inventory_lots",
            alt_name: null,
            remote_field: "received_uom_unit_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "items",
          alt_name: null,
          fields: [
            "id",
            "number",
            "uom_type_id",
            "preferred_bin_id",
            "item_expires",
          ],
          condition: {
            remote_table: "inventory_lots",
            alt_name: null,
            remote_field: "item_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "inventory_statuses",
          alt_name: null,
          fields: ["id", "name"],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "status_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "locations",
          alt_name: null,
          fields: ["id", "name"],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "location_id",
            local_field: "id",
          },
          outer: false,
        },
      ],
      filters: (() => {
        if (mode.label.includes("Release")) {
          return [
            [
              {
                table: "inventory_lots",
                alt_name: null,
                field: "lot_number",
                operator: "~",
                value: "^\\d{2}-\\d{6}",
                is_not: mode.label.includes("Built"),
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
              {
                table: "inventory_positions",
                alt_name: null,
                field: "quantity_on_hand",
                operator: ">",
                value: 0,
                is_not: false,
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
              {
                table: "inventory_statuses",
                alt_name: null,
                field: "name",
                operator: "==",
                value: ["Pending Quality Review", "Received"],
                is_not: false,
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
            ],
          ];
        } else {
          return [
            [
              {
                table: "inventory_lots",
                alt_name: null,
                field: "lot_number",
                operator: "~",
                value: "^\\d{2}-\\d{6}",
                is_not: false,
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
              {
                table: "inventory_positions",
                alt_name: null,
                field: "quantity_on_hand",
                operator: ">",
                value: 0,
                is_not: false,
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
              {
                table: "inventory_statuses",
                alt_name: null,
                field: "name",
                operator: "==",
                value: isTo ? "Received" : "Awaiting Inspection",
                is_not: false,
                inclusive_start: false,
                inclusive_end: false,
                case_insensitive: false,
              },
            ],
          ];
        }
      })(),
      sorts: [
        {
          table: "items",
          alt_name: null,
          field: "number",
          asc: true,
        },
        {
          table: "inventory_lots",
          alt_name: null,
          field: "lot_number",
          asc: false,
        },
      ],
      subqueries: [],
    },
    open && (mode.label.includes("Release") || mode.label.includes("Inspect")),
  );
  const relInspectFromOpts: APosOption[] = useMemo(() => {
    if (relInspectPosResponse?.items) {
      return relInspectPosResponse.items.map((pos) => ({
        label: `${pos["items.number"]}: ${pos["inventory_lots.lot_number"]}`,
        type: "invposition",
        value: {
          id: pos["inventory_positions.id"],
          lotId: pos["inventory_lots.id"],
          lotNum: pos["inventory_lots.lot_number"],
          lotRecUnitId: pos["uom_units.id"],
          lotRecUnitSymbol: pos["uom_units.unit_symbol"],
          lotRecUnitConv: parseFloat(pos["uom_units.conversion_factor"]),
          lotRelRev: "NOT RELEASED",
          lotInitRev: pos["inventory_lots.initial_revision"],
          lotExpDate: pos["inventory_lots.expiration_date"],
          lotRFT: pos["inventory_lots.right_first_time"] || false,
          lotRWRK: pos["inventory_lots.rework"] || false,
          lotPD: pos["inventory_lots.planned_deviation"] || false,
          itemId: pos["items.id"],
          itemNum: pos["items.number"],
          itemTypeId: pos["items.uom_type_id"],
          itemPrefBin: pos["items.preferred_bin_id"],
          itemExpires: pos["items.item_expires"],
          locId: pos["locations.id"],
          locName: pos["locations.name"],
          statId: pos["inventory_statuses.id"],
          statName: pos["inventory_statuses.name"],
          quant: parseFloat(pos["inventory_positions.quantity_on_hand"]),
        },
      }));
    } else {
      return [];
    }
  }, [relInspectPosResponse]);

  const {
    data: posResponse,
    isLoading: posLoading,
    error: posError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "inventory_positions",
      alt_name: null,
      primary_fields: ["id", "quantity_on_hand"],
      joins: [
        {
          table: "inventory_lots",
          alt_name: null,
          fields: ["id", "lot_number"],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "inventory_lot_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "items",
          alt_name: null,
          fields: ["id"],
          condition: {
            remote_table: "inventory_lots",
            alt_name: null,
            remote_field: "item_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "inventory_statuses",
          alt_name: null,
          fields: ["id", "name"],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "status_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "locations",
          alt_name: null,
          fields: ["id", "name"],
          condition: {
            remote_table: "inventory_positions",
            alt_name: null,
            remote_field: "location_id",
            local_field: "id",
          },
          outer: false,
        },
      ],
      filters: (() => {
        const filts: DynamicRequestFilter[][] = [];
        if (thisMovementData.lot) {
          filts.push([
            {
              table: "inventory_positions",
              alt_name: null,
              field: "inventory_lot_id",
              operator: "==",
              value: thisMovementData.lot.value.id,
              is_not: false,
              inclusive_start: false,
              inclusive_end: false,
              case_insensitive: false,
            },
          ]);
        } else if (thisMovementData.item) {
          filts.push([
            {
              table: "inventory_lots",
              alt_name: null,
              field: "item_id",
              operator: "==",
              value: thisMovementData.item.value.id,
              is_not: false,
              inclusive_start: false,
              inclusive_end: false,
              case_insensitive: false,
            },
          ]);
        } else {
          return filts;
        }
        filts[0].push({
          table: "inventory_positions",
          alt_name: null,
          field: "quantity_on_hand",
          operator: ">",
          value: 0,
          is_not: false,
          inclusive_start: false,
          inclusive_end: false,
          case_insensitive: false,
        });
        return filts;
      })(),
      sorts: [
        {
          table: "inventory_lots",
          alt_name: null,
          field: "lot_number",
          asc: false,
        },
      ],
      subqueries: [],
    },
    open &&
      !mode.label.includes("Release") &&
      !mode.label.includes("Inspect") &&
      (!!thisMovementData.item || !!thisMovementData.lot),
  );
  const validFromPosOpts: APosOption[] = useMemo(() => {
    if (posResponse?.items) {
      return posResponse.items.map((pos) => ({
        label: `${pos["inventory_lots.lot_number"]}: ${pos["locations.name"]} - ${pos["inventory_statuses.name"]}`,
        type: "invposition",
        value: {
          id: pos["inventory_positions.id"],
          lotId: pos["inventory_lots.id"],
          lotNum: "",
          lotRecUnitId: "",
          lotRecUnitSymbol: "",
          lotRecUnitConv: 1,
          lotRelRev: pos["inventory_lots.released_revision"] || "NOT RELEASED",
          lotInitRev: pos["inventory_lots.initial_revision"],
          lotExpDate: "",
          lotRFT: false,
          lotRWRK: false,
          lotPD: false,
          itemId: pos["items.id"],
          itemNum: "",
          itemTypeId: "",
          itemPrefBin: "",
          itemExpires: false,
          locId: pos["locations.id"],
          locName: pos["locations.name"],
          statId: pos["inventory_statuses.id"],
          statName: pos["inventory_statuses.name"],
          quant: parseFloat(pos["inventory_positions.quantity_on_hand"]),
        },
      }));
    } else {
      return [];
    }
  }, [posResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "uom_units",
    alt_name: null,
    primary_fields: ["id", "uom_type_id", "unit_symbol", "conversion_factor"],
    joins: [],
    filters: [],
    sorts: [
      {
        table: "uom_units",
        alt_name: null,
        field: "uom_type_id",
        asc: true,
      },
      {
        table: "uom_units",
        alt_name: null,
        field: "unit_symbol",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const uomOpts: { [key: string]: AUnitOption[] } = useMemo(() => {
    if (unitsResponse?.items) {
      const opts: { [key: string]: AUnitOption[] } = {};
      unitsResponse.items.forEach((unit) => {
        if (opts[unit["uom_units.uom_type_id"]] === undefined) {
          opts[unit["uom_units.uom_type_id"]] = [];
        }
        opts[unit["uom_units.uom_type_id"]].push({
          label: unit["uom_units.unit_symbol"],
          type: "unit",
          value: {
            id: unit["uom_units.id"],
            uomTypeId: unit["uom_units.uom_type_id"],
            conv: parseFloat(unit["uom_units.conversion_factor"]),
          },
        });
      });
      return opts;
    } else {
      return {};
    }
  }, [unitsResponse]);

  const {
    data: ncResponse,
    isLoading: ncsLoading,
    error: ncError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "non_conformances",
      alt_name: null,
      primary_fields: ["id", "nc_number"],
      joins: [],
      filters: [],
      sorts: [
        {
          table: "non_conformances",
          alt_name: null,
          field: "nc_number",
          asc: false,
        },
      ],
      subqueries: [],
    },
    mode.label.includes("Built"),
  );
  const ncOpts: ANonConOption[] = useMemo(() => {
    console.log("UPDATING NON CON OPTS", ncResponse, newNcOpts);
    let opts: ANonConOption[] = [];
    if (ncResponse?.items) {
      opts = ncResponse.items.map((nc) => ({
        label: nc["non_conformances.nc_number"],
        type: "noncon",
        value: {
          id: nc["non_conformances.id"],
        },
      }));
    }
    opts.unshift({
      label: "New NonCon",
      type: "noncon",
      value: { id: "newopt" },
    });
    opts = [...opts, ...newNcOpts];
    return opts;
  }, [ncResponse, newNcOpts]);
  const filteredNcOpts: ANonConOption[] = useMemo(() => {
    console.log("FILTERING NON CON OPTIONS");
    if (thisMovementData.ncs.length === 0) {
      return ncOpts;
    } else {
      return ncOpts.filter((o) => {
        for (let nc of thisMovementData.ncs) {
          if (o.value.id === nc.value.id) {
            return false;
          }
        }
        return true;
      });
    }
  }, [ncOpts, thisMovementData.ncs]);

  const handleReleaseFromPosChange = (value: APosOption | null) => {
    if (!!value) {
      const locOpt = mode.label.includes("Release")
        ? locOpts.find((o) => o.value.id === value.value.itemPrefBin) || null
        : quarLoc;
      const uomOpt =
        uomOpts[value.value.itemTypeId]?.find(
          (o) => o.value.id === value.value.lotRecUnitId,
        ) || null;
      console.log("Selected Position Data", value);
      setThisMovementData((prev) => ({
        ...prev,
        fromPos: value,
        uom: uomOpt,
        toLoc: locOpt,
        toStat: mode.label.includes("Release")
          ? releaseStat
          : isTo
            ? inspectStat
            : pendReviewStat,
        expirationDate: value.value.lotExpDate,
        rft: value.value.lotRFT,
        rw: value.value.lotRWRK,
        pd: value.value.lotPD,
        releaseRev: value.value.lotInitRev,
        quantity: mode.label.includes("Inspect")
          ? value.value.quant / value.value.lotRecUnitConv
          : 0,
      }));
    } else {
      setThisMovementData((prev) => ({
        ...prev,
        fromPos: value,
        uom: null,
        toLoc: null,
        toStat: mode.label.includes("Release")
          ? releaseStat
          : isTo
            ? inspectStat
            : pendReviewStat,
        expirationDate: null,
        rft: false,
        rw: false,
        pd: false,
        releaseRev: "",
        quantity: 0,
      }));
    }
  };

  const handleDialogSubmit = async () => {
    try {
      const results = await movSchema.validate(thisMovementData, {
        context: {
          movType: mode,
          isTo: isTo,
          availQuant: thisMovementData.fromPos?.value.quant || 0,
        },
        abortEarly: false,
      });
      console.log("Validation Results", results);
      onDialogSubmit(thisMovementData);
      handleDialogClose(null, null);
    } catch (e: any) {
      console.error("Schema Validation Error", {
        name: e["name"],
        type: e["type"],
        value: e["value"],
        path: e["path"],
        errors: e["errors"],
      });
    }
  };

  const handleDialogClose = (event: any, reason: string | null) => {
    if (reason === "backdropClick") {
      return;
    }
    setThisMovementData({
      fromPos: null,
      toLoc: null,
      toStat: null,
      lot: null,
      item: null,
      uom: null,
      releaseRev: "",
      expirationDate: null,
      quantity: 0,
      notes: null,
      rft: false,
      rw: false,
      pd: false,
      ncs: [],
    });
    setDialogErrors({
      general: [],
      fromPos: null,
      toLoc: null,
      toStat: null,
      lot: null,
      item: null,
      uom: null,
      releaseRev: null,
      quantity: null,
      notes: null,
      rft: null,
      rw: null,
      pd: null,
      ncs: null,
      newNcNum: null,
    });
    setCurrentNcOpt(null);
    setNewNcNum("");
    onDialogClose();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode.label.includes("Release")
          ? "Inventory Release Dialog"
          : "Inventory Transfer Dialog"}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            width: "100%",
          }}
        >
          {superUser && (
            <Button
              sx={{ justifySelf: "flex-start" }}
              onClick={() =>
                console.log({
                  movData: thisMovementData,
                  itemOpts: itemOpts,
                  lotOpts: lotOpts,
                  unitOpts: uomOpts,
                })
              }
            >
              DEBUG PRINT
            </Button>
          )}
        </Box>
        {mode.label.includes("Release") ? (
          <Stack direction="column" gap={1}>
            <Box display="flex" gap={1}>
              {pendReleasePosLoading ? (
                <Box display="flex" gap={1}>
                  <CircularProgress />
                  <Typography variant="body1">Loading...</Typography>
                </Box>
              ) : relInspectFromOpts.length === 0 ? (
                <Typography variant="body1" color="error">
                  There are currently no Lots pending Release.
                </Typography>
              ) : (
                <Autocomplete
                  fullWidth
                  value={thisMovementData.fromPos}
                  options={relInspectFromOpts}
                  onChange={(event, newValue: APosOption | null) => {
                    handleReleaseFromPosChange(newValue);
                  }}
                  getOptionLabel={(option: APosOption) => option.label}
                  isOptionEqualToValue={(
                    option: APosOption,
                    value: APosOption,
                  ) => option.value.id === value.value.id}
                  disabled={pendReleasePosLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lot To Release"
                      error={!!dialogErrors.fromPos}
                      helperText={dialogErrors.fromPos}
                    />
                  )}
                />
              )}
            </Box>
            {!thisMovementData.fromPos ? (
              <Typography variant="h5" color="error">
                Select a Lot to Release
              </Typography>
            ) : (
              <Stack direction="column" gap={1}>
                <Autocomplete
                  fullWidth
                  value={thisMovementData.toLoc}
                  options={locOpts}
                  onChange={(event, newValue: ALocOption | null) => {
                    setThisMovementData((prev) => ({
                      ...prev,
                      toLoc: newValue,
                    }));
                  }}
                  getOptionLabel={(option: ALocOption) => option.label}
                  isOptionEqualToValue={(
                    option: ALocOption,
                    value: ALocOption,
                  ) => option.value.id === value.value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location to Release To"
                      error={!!dialogErrors.fromPos}
                      helperText={dialogErrors.fromPos}
                    />
                  )}
                />
                <Box display="flex" gap={1}>
                  <Stack direction="column" gap={1} width="50%">
                    <Typography variant="h5">Production Review Data</Typography>
                    <TextField
                      label="Quantity Pending Release"
                      value={`${Number(
                        (
                          thisMovementData.fromPos.value.quant /
                          thisMovementData.fromPos.value.lotRecUnitConv
                        ).toFixed(4),
                      )}x ${thisMovementData.fromPos.value.lotRecUnitSymbol}`}
                      disabled={true}
                      fullWidth
                    />
                    <TextField
                      label="Revision Pending Release"
                      value={thisMovementData.fromPos.value.lotInitRev}
                      disabled={true}
                      fullWidth
                    />
                    {thisMovementData.fromPos.value.itemExpires && (
                      <>
                        {!thisMovementData.fromPos.value.lotExpDate ? (
                          <Typography
                            variant="h6"
                            flexWrap={"wrap"}
                            color="error"
                          >
                            Lot is missing Expiration Date
                          </Typography>
                        ) : (
                          <DatePicker
                            label="Current Lot Expiration Date"
                            value={
                              new Date(
                                thisMovementData.fromPos.value.lotExpDate,
                              )
                            }
                            disabled={true}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      </>
                    )}
                    {mode.label.includes("Built") && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.fromPos.value.lotRFT}
                              disabled={true}
                            />
                          }
                          label="Current Right First Time?"
                          labelPlacement="top"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.fromPos.value.lotRWRK}
                              disabled={true}
                            />
                          }
                          label="Current Rework?"
                          labelPlacement="top"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.fromPos.value.lotPD}
                              disabled={true}
                            />
                          }
                          label="Current Planned Deviation?"
                          labelPlacement="top"
                        />
                      </>
                    )}
                  </Stack>
                  <Stack direction="column" gap={1} width="50%">
                    <Typography variant="h5">Confirmed Release Data</Typography>
                    <NumericEntryField
                      value={thisMovementData.quantity}
                      onChange={(value) => {
                        setThisMovementData((prev) => ({
                          ...prev,
                          quantity: value,
                        }));
                      }}
                      decimalPlaces={4}
                      isCurrency={false}
                      endAdornment={
                        thisMovementData.fromPos.value.lotRecUnitSymbol
                      }
                      min={0}
                      label="Released Quantity"
                      textFieldProps={{
                        fullWidth: true,
                        error: !!dialogErrors.quantity,
                        helperText: dialogErrors.quantity,
                      }}
                    />
                    <TextField
                      label="Released Revision"
                      value={thisMovementData.releaseRev}
                      onChange={(e) => {
                        setThisMovementData((prev) => ({
                          ...prev,
                          releaseRev: e.target.value,
                        }));
                      }}
                      fullWidth
                      error={!!dialogErrors.releaseRev}
                      helperText={dialogErrors.releaseRev}
                    />
                    {thisMovementData.fromPos.value.itemExpires && (
                      <DatePicker
                        label="Confirmed Expiration Date"
                        value={
                          !!thisMovementData.expirationDate
                            ? new Date(thisMovementData.expirationDate)
                            : null
                        }
                        onChange={(date) => {
                          setThisMovementData((prev) => ({
                            ...prev,
                            expirationDate: date?.toISOString() || null,
                          }));
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                    {mode.label.includes("Built") && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.rft}
                              onChange={(e, c) =>
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  rft: c,
                                }))
                              }
                            />
                          }
                          label="Confirmed Right First Time?"
                          labelPlacement="top"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.rw}
                              onChange={(e, c) =>
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  rw: c,
                                }))
                              }
                            />
                          }
                          label="Confirmed Rework?"
                          labelPlacement="top"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={thisMovementData.pd}
                              onChange={(e, c) =>
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  pd: c,
                                }))
                              }
                            />
                          }
                          label="Confirmed Planned Deviation?"
                          labelPlacement="top"
                        />
                      </>
                    )}
                  </Stack>
                </Box>
                {mode.label.includes("Built") && (
                  <>
                    <Typography variant="h5" textAlign={"center"}>
                      Non-Conformances
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Stack direction="column" gap={1} width="50%">
                        <Autocomplete
                          value={currentNcOpt}
                          options={filteredNcOpts}
                          onChange={(e, v) => {
                            setCurrentNcOpt(v);
                          }}
                          getOptionLabel={(option: ANonConOption) =>
                            option.label
                          }
                          fullWidth
                          isOptionEqualToValue={(
                            option: ANonConOption,
                            value: ANonConOption,
                          ) => option.value.id === value.value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select a Non-Conformance"
                              error={!!dialogErrors.fromPos}
                              helperText={dialogErrors.fromPos}
                            />
                          )}
                        />
                        {currentNcOpt?.label === "New NonCon" && (
                          <TextField
                            value={newNcNum}
                            onChange={(e) => setNewNcNum(e.target.value)}
                            label="New Non-Conformance Number"
                            fullWidth
                            error={!!dialogErrors.newNcNum}
                            helperText={dialogErrors.newNcNum}
                          />
                        )}
                        <Button
                          variant="contained"
                          color="info"
                          onClick={() => {
                            if (!!currentNcOpt) {
                              if (currentNcOpt.label === "New NonCon") {
                                for (let newNc of newNcOpts) {
                                  if (`${newNcNum}*` === newNc.label) {
                                    setDialogErrors((prev) => ({
                                      ...prev,
                                      newNcNum: "New NC Number already exists",
                                    }));
                                    return;
                                  }
                                }
                                setDialogErrors((prev) => ({
                                  ...prev,
                                  newNcNum: null,
                                }));
                                const newNcOpt: ANonConOption = {
                                  label: `${newNcNum}*`,
                                  type: "noncon",
                                  value: { id: `new:${newNcNum}` },
                                };
                                setNewNcOpts((prev) => [...prev, newNcOpt]);
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  ncs: [...prev.ncs, newNcOpt],
                                }));
                              } else {
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  ncs: [...prev.ncs, currentNcOpt],
                                }));
                              }
                              setCurrentNcOpt(null);
                              setNewNcNum("");
                            }
                          }}
                          disabled={
                            !currentNcOpt ||
                            (currentNcOpt.label === "New NonCon" &&
                              newNcNum === "")
                          }
                        >
                          Link NC
                        </Button>
                      </Stack>
                      <Box
                        display="grid"
                        gridTemplateColumns={"1fr 1fr"}
                        gridAutoRows={"auto"}
                        columnGap={1}
                        rowGap={1}
                        width="50%"
                        border={"black 1px solid"}
                        height={Math.max(
                          50,
                          Math.floor(thisMovementData.ncs.length / 2) * 50,
                        )}
                        sx={{
                          background: "#a6e3ffff",
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                        alignItems={"flex-start"}
                        pl={2}
                        pr={2}
                      >
                        {thisMovementData.ncs.map((nc, index) => (
                          <Box
                            display="flex"
                            alignContent={"center"}
                            // alignItems={"center"}
                            // justifyItems={"center"}
                            justifyContent={"center"}
                            width="100%"
                            height={50}
                            border={"black 1px solid"}
                          >
                            <Chip
                              key={index}
                              label={nc.label}
                              color="default"
                              sx={{
                                gridColumn: index % 2 === 0 ? 1 : 2,
                                gridRow: Math.floor(index / 2) + 1,
                              }}
                              onDelete={() =>
                                setThisMovementData((prev) => {
                                  let ncs = prev.ncs;
                                  ncs.splice(index, 1);
                                  return {
                                    ...prev,
                                    ncs: ncs,
                                  };
                                })
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        ) : mode.label === "Adjustment" ? (
          <Stack direction="column" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isTo}
                  onChange={(event, checked) => {
                    setIsTo(checked);
                    setThisMovementData((prev) => ({
                      ...prev,
                      lot: null,
                    }));
                  }}
                />
              }
              label={isTo ? "Adding Inventory" : "Removing Inventory"}
              labelPlacement="end"
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Autocomplete
                fullWidth
                value={thisMovementData.item}
                options={itemOpts}
                onChange={(event, newValue: AItemOption | null) => {
                  setThisMovementData((prev) => ({
                    ...prev,
                    item: newValue,
                    lot: null,
                    uom: newValue
                      ? uomOpts[newValue.value.uomTypeId].find(
                          (unit) => unit.value.id === newValue.value.storeId,
                        ) || null
                      : null,
                  }));
                }}
                getOptionLabel={(option: AItemOption) => option.label}
                isOptionEqualToValue={(
                  option: AItemOption,
                  value: AItemOption,
                ) => option.value.id === value.value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter By Item"
                    error={!!dialogErrors.item}
                    helperText={dialogErrors.item}
                  />
                )}
              />
              <Autocomplete
                fullWidth
                value={thisMovementData.lot}
                disabled={!thisMovementData.item}
                options={lotOpts}
                onChange={(event, newValue: ALotOption | null) => {
                  setThisMovementData((prev) => ({
                    ...prev,
                    lot: newValue,
                  }));
                }}
                getOptionLabel={(option: ALotOption) => option.label}
                isOptionEqualToValue={(option: ALotOption, value: ALotOption) =>
                  option.value.id === value.value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lot To Move"
                    error={!!dialogErrors.lot}
                    helperText={dialogErrors.lot}
                  />
                )}
              />
            </Box>
            {!thisMovementData.item || !thisMovementData.lot ? (
              <Typography variant="h5">
                Select an Item and Lot to Adjust Inventory for
              </Typography>
            ) : (
              <>
                {isTo ? (
                  <Stack direction="column" gap={1}>
                    <Box display="flex" gap={1}>
                      <Autocomplete
                        fullWidth
                        value={thisMovementData.toLoc}
                        options={locOpts}
                        onChange={(event, newValue: ALocOption | null) => {
                          setThisMovementData((prev) => ({
                            ...prev,
                            toLoc: newValue,
                          }));
                        }}
                        getOptionLabel={(option: ALocOption) => option.label}
                        isOptionEqualToValue={(
                          option: ALocOption,
                          value: ALocOption,
                        ) => option.value.id === value.value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="To BIN"
                            error={!!dialogErrors.toLoc}
                            helperText={dialogErrors.toLoc}
                          />
                        )}
                      />
                      <Autocomplete
                        fullWidth
                        value={thisMovementData.toStat}
                        options={statOpts}
                        onChange={(event, newValue: AInvStatOption | null) => {
                          setThisMovementData((prev) => ({
                            ...prev,
                            toStat: newValue,
                          }));
                        }}
                        getOptionLabel={(option: AInvStatOption) =>
                          option.label
                        }
                        isOptionEqualToValue={(
                          option: AInvStatOption,
                          value: AInvStatOption,
                        ) => option.value.id === value.value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="To Status"
                            error={!!dialogErrors.toStat}
                            helperText={dialogErrors.toStat}
                          />
                        )}
                      />
                    </Box>
                    {!thisMovementData.toLoc || !thisMovementData.toStat ? (
                      <Typography variant="h5">
                        Select a Destination BIN and Status to enter Quantity
                      </Typography>
                    ) : (
                      <Box display="flex" gap={1}>
                        <Autocomplete
                          fullWidth
                          value={thisMovementData.uom}
                          options={
                            !!thisMovementData.item
                              ? uomOpts[thisMovementData.item.value.uomTypeId]
                              : []
                          }
                          onChange={(event, newValue: AUnitOption | null) => {
                            setThisMovementData((prev) => ({
                              ...prev,
                              uom: newValue,
                            }));
                          }}
                          getOptionLabel={(option: AUnitOption) => option.label}
                          isOptionEqualToValue={(
                            option: AUnitOption,
                            value: AUnitOption,
                          ) => option.value.id === value.value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="UoM"
                              error={!!dialogErrors.uom}
                              helperText={dialogErrors.uom}
                            />
                          )}
                        />
                        {!thisMovementData.uom ? (
                          <Typography variant="body1">
                            Select a UOM to enter Quantity
                          </Typography>
                        ) : (
                          <NumericEntryField
                            value={thisMovementData.quantity}
                            onChange={(val) =>
                              setThisMovementData((prev) => ({
                                ...prev,
                                quantity: val,
                              }))
                            }
                            decimalPlaces={4}
                            isCurrency={false}
                            endAdornment={thisMovementData.uom?.label}
                            min={0}
                            label="Quantity Moving"
                            textFieldProps={{
                              fullWidth: true,
                              size: "small",
                              disabled: !!thisMovementData.uom,
                              error: !!dialogErrors.quantity,
                              helperText: dialogErrors.quantity,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <>
                    {validFromPosOpts.length === 0 ? (
                      <Typography variant="h5">
                        No available Positions to Adjust for the selected Item
                        and Lot.
                      </Typography>
                    ) : (
                      <Stack direction="column" gap={1}>
                        <Autocomplete
                          fullWidth
                          value={thisMovementData.fromPos}
                          options={validFromPosOpts}
                          onChange={(event, newValue: APosOption | null) => {
                            setThisMovementData((prev) => ({
                              ...prev,
                              fromPos: newValue,
                            }));
                          }}
                          getOptionLabel={(option: APosOption) => option.label}
                          isOptionEqualToValue={(
                            option: APosOption,
                            value: APosOption,
                          ) => option.value.id === value.value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Position to Adjust"
                              error={!!dialogErrors.fromPos}
                              helperText={dialogErrors.fromPos}
                            />
                          )}
                        />
                        {!thisMovementData.fromPos ? (
                          <Typography variant="h5">
                            Select a Position to Enter Quantity
                          </Typography>
                        ) : (
                          <Box display="flex" gap={1}>
                            <Autocomplete
                              fullWidth
                              value={thisMovementData.uom}
                              options={
                                !!thisMovementData.item
                                  ? uomOpts[
                                      thisMovementData.item.value.uomTypeId
                                    ]
                                  : []
                              }
                              onChange={(
                                event,
                                newValue: AUnitOption | null,
                              ) => {
                                setThisMovementData((prev) => ({
                                  ...prev,
                                  uom: newValue,
                                }));
                              }}
                              getOptionLabel={(option: AUnitOption) =>
                                option.label
                              }
                              isOptionEqualToValue={(
                                option: AUnitOption,
                                value: AUnitOption,
                              ) => option.value.id === value.value.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="UoM"
                                  error={!!dialogErrors.uom}
                                  helperText={dialogErrors.uom}
                                />
                              )}
                            />
                            {!thisMovementData.uom ? (
                              <Typography variant="body1">
                                Select a UOM to enter Quantity
                              </Typography>
                            ) : (
                              <Stack direction="column" gap={0.5} width="50%">
                                <TextField
                                  label="Quantity Available In Selected Position:"
                                  value={`${
                                    thisMovementData.fromPos?.value.quant /
                                    thisMovementData.uom?.value.conv
                                  }x ${thisMovementData.uom.label}`}
                                  fullWidth
                                  variant="standard"
                                  disabled={true}
                                />
                                <NumericEntryField
                                  value={thisMovementData.quantity}
                                  onChange={(val) =>
                                    setThisMovementData((prev) => ({
                                      ...prev,
                                      quantity: val,
                                    }))
                                  }
                                  decimalPlaces={4}
                                  isCurrency={false}
                                  endAdornment={thisMovementData.uom?.label}
                                  min={0}
                                  label="Quantity Moving"
                                  textFieldProps={{
                                    fullWidth: true,
                                    size: "small",
                                    disabled: !!thisMovementData.uom,
                                    error: !!dialogErrors.quantity,
                                    helperText: dialogErrors.quantity,
                                  }}
                                />
                              </Stack>
                            )}
                          </Box>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </>
            )}
          </Stack>
        ) : mode.label === "Receiving Inspection" ? (
          <Stack direction="column" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isTo}
                  onChange={(event, checked) => {
                    setIsTo(checked);
                    setThisMovementData((prev) => ({
                      ...prev,
                      lot: null,
                      toLoc: quarLoc,
                      toStat: checked ? inspectStat : pendReviewStat,
                    }));
                  }}
                />
              }
              label={isTo ? "Beginning Inspection" : "Inspection Complete"}
              labelPlacement="end"
            />
            {pendReleasePosLoading ? (
              <Box display="flex" gap={1}>
                <CircularProgress />
                <Typography variant="body1">Loading...</Typography>
              </Box>
            ) : relInspectFromOpts.length === 0 ? (
              <Typography variant="body1" color="error">
                {isTo
                  ? "There are currently no Received Lots."
                  : "There are current no Inspected Lots."}
              </Typography>
            ) : (
              <Autocomplete
                fullWidth
                value={thisMovementData.fromPos}
                options={relInspectFromOpts}
                onChange={(event, newValue: APosOption | null) => {
                  handleReleaseFromPosChange(newValue);
                }}
                getOptionLabel={(option: APosOption) => option.label}
                isOptionEqualToValue={(option: APosOption, value: APosOption) =>
                  option.value.id === value.value.id
                }
                disabled={pendReleasePosLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      isTo
                        ? "Lot to Mark for Inspection"
                        : "Lot To Mark for Release"
                    }
                    error={!!dialogErrors.fromPos}
                    helperText={dialogErrors.fromPos}
                  />
                )}
              />
            )}
          </Stack>
        ) : (
          <Stack direction="column" gap={1}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Autocomplete
                fullWidth
                value={thisMovementData.item}
                options={itemOpts}
                onChange={(event, newValue: AItemOption | null) => {
                  setThisMovementData((prev) => ({
                    ...prev,
                    item: newValue,
                    lot: null,
                    uom: newValue
                      ? uomOpts[newValue.value.uomTypeId].find(
                          (unit) => unit.value.id === newValue?.value.storeId,
                        ) || null
                      : null,
                  }));
                }}
                getOptionLabel={(option: AItemOption) => option.label}
                isOptionEqualToValue={(
                  option: AItemOption,
                  value: AItemOption,
                ) => option.value.id === value.value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter By Item"
                    error={!!dialogErrors.item}
                    helperText={dialogErrors.item}
                  />
                )}
              />
              <Autocomplete
                fullWidth
                value={thisMovementData.lot}
                disabled={!thisMovementData.item}
                options={lotOpts}
                onChange={(event, newValue: ALotOption | null) => {
                  setThisMovementData((prev) => ({
                    ...prev,
                    lot: newValue,
                  }));
                }}
                getOptionLabel={(option: ALotOption) => option.label}
                isOptionEqualToValue={(option: ALotOption, value: ALotOption) =>
                  option.value.id === value.value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lot To Move"
                    error={!!dialogErrors.lot}
                    helperText={dialogErrors.lot}
                  />
                )}
              />
            </Box>
            {!thisMovementData.item || !thisMovementData.lot ? (
              <Typography variant="h5">
                Select an Item and Lot to view Existing Positions
              </Typography>
            ) : posLoading ? (
              <Box display="flex" gap={1}>
                <CircularProgress />
                <Typography variant="body1">Loading...</Typography>
              </Box>
            ) : validFromPosOpts.length === 0 ? (
              <Typography variant="h5" color="error">
                No available Positions for selected Item and Lot
              </Typography>
            ) : (
              <Autocomplete
                fullWidth
                value={thisMovementData.fromPos}
                options={validFromPosOpts}
                onChange={(event, newValue: APosOption | null) => {
                  setThisMovementData((prev) => ({
                    ...prev,
                    fromPos: newValue,
                  }));
                }}
                getOptionLabel={(option: APosOption) => option.label}
                isOptionEqualToValue={(option: APosOption, value: APosOption) =>
                  option.value.id === value.value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Origin Position"
                    error={!!dialogErrors.fromPos}
                    helperText={dialogErrors.fromPos}
                  />
                )}
              />
            )}
            {!thisMovementData.fromPos ? (
              <Typography variant="h5">
                Select an Origin Position to transfer from
              </Typography>
            ) : (
              <Stack direction="column" gap={1}>
                <Box display="flex" gap={1}>
                  <Autocomplete
                    fullWidth
                    value={thisMovementData.toLoc}
                    options={locOpts}
                    onChange={(event, newValue: ALocOption | null) => {
                      setThisMovementData((prev) => ({
                        ...prev,
                        toLoc: newValue,
                      }));
                    }}
                    getOptionLabel={(option: ALocOption) => option.label}
                    isOptionEqualToValue={(
                      option: ALocOption,
                      value: ALocOption,
                    ) => option.value.id === value.value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To BIN"
                        error={!!dialogErrors.toLoc}
                        helperText={dialogErrors.toLoc}
                      />
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    value={thisMovementData.toStat}
                    options={statOpts}
                    onChange={(event, newValue: AInvStatOption | null) => {
                      setThisMovementData((prev) => ({
                        ...prev,
                        toStat: newValue,
                      }));
                    }}
                    getOptionLabel={(option: AInvStatOption) => option.label}
                    isOptionEqualToValue={(
                      option: AInvStatOption,
                      value: AInvStatOption,
                    ) => option.value.id === value.value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To Status"
                        error={!!dialogErrors.toStat}
                        helperText={dialogErrors.toStat}
                      />
                    )}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <Autocomplete
                    fullWidth
                    value={thisMovementData.uom}
                    options={
                      !!thisMovementData.item
                        ? uomOpts[thisMovementData.item.value.uomTypeId]
                        : []
                    }
                    onChange={(event, newValue: AUnitOption | null) => {
                      setThisMovementData((prev) => ({
                        ...prev,
                        uom: newValue,
                      }));
                    }}
                    getOptionLabel={(option: AUnitOption) => option.label}
                    isOptionEqualToValue={(
                      option: AUnitOption,
                      value: AUnitOption,
                    ) => option.value.id === value.value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="UoM"
                        error={!!dialogErrors.uom}
                        helperText={dialogErrors.uom}
                      />
                    )}
                  />
                  {!thisMovementData.uom ? (
                    <Typography variant="body1">
                      Select a UOM to enter Quantity
                    </Typography>
                  ) : (
                    <Stack direction="column" gap={0.5} width="50%">
                      <Typography variant="body1">{`Quantity Available: ${
                        thisMovementData.fromPos?.value.quant /
                        thisMovementData.uom?.value.conv
                      }x ${thisMovementData.uom.label}`}</Typography>
                      <NumericEntryField
                        value={thisMovementData.quantity}
                        onChange={(val) =>
                          setThisMovementData((prev) => ({
                            ...prev,
                            quantity: val,
                          }))
                        }
                        decimalPlaces={4}
                        isCurrency={false}
                        endAdornment={thisMovementData.uom?.label}
                        min={0}
                        label="Quantity Moving"
                        textFieldProps={{
                          fullWidth: true,
                          size: "small",
                          disabled: !!thisMovementData.uom,
                          error: !!dialogErrors.quantity,
                          helperText: dialogErrors.quantity,
                        }}
                      />
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={(e) => handleDialogClose(e, "Cancel Clicked")}
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDialogSubmit}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PageInvMovForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";

  const createMovMutation = useTSMCreateInvMovement();
  const createBatchMovMutation = useTSMCreateInvMovementBatch();
  const updateMovMutation = useTSMUpdateInvMovement();

  // State
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [curDialogData, setCurDialogData] = useState<newMovementRequest | null>(
    null,
  );
  const [curDialogIndex, setCurDialogIndex] = useState<number>(-1);

  const { user } = useAuth();

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<InvMovFormData>({
    resolver: yupResolver(fullFormSchema) as any,
    defaultValues: defaultValues,
  });

  const [watchedMovType, watchedMovements] = watch([
    "movement_type",
    "movements",
  ]);

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "movements",
  });

  // Form submission
  const onSubmit = async (data: InvMovFormData) => {
    try {
      setError(null);
      if (formMode === "create") {
        const batchPayload: InvMovBatchCreate = {
          movement_type: data.movement_type?.value.id! as InvMovementType,
          notes: data.notes || undefined,
          movements: data.movements.map((dataLine) => {
            const movPayload: InvMovCreate = {
              from_position_id: dataLine.fromPos?.value.id || null,
              to_location_id: dataLine.toLoc?.value.id || null,
              to_status_id: dataLine.toStat?.value.id || null,
              lot_id:
                dataLine.lot?.value.id || dataLine.fromPos?.value.lotId || null,
              uom_unit_id: dataLine.uom?.value.id!,
              quantity: dataLine.quantity,
              release_rev: dataLine.releaseRev || null,
              movement_type: data.movement_type?.value.id! as InvMovementType,
              notes:
                dataLine.notes || data.movement_type?.value.id === "ADJUSTMENT"
                  ? data.notes || "NO NOTES PROVIDED"
                  : null,
              expiration_date: dataLine.expirationDate,
              rft: dataLine.rft,
              rw: dataLine.rw,
              pd: dataLine.pd,
              ncs_to_link: dataLine.ncs
                .filter((nc) => !nc.value.id.includes("new:"))
                .map((nc) => nc.value.id),
              ncs_to_create: dataLine.ncs
                .filter((nc) => nc.value.id.includes("new:"))
                .map((nc) => nc.value.id),
              associated_id: null,
              associated_table: null,
            };
            return movPayload;
          }),
        };
        await createBatchMovMutation.mutateAsync(batchPayload);
        handleCancel();
      } else {
        setError(`Processing failed`);
      }
    } catch (err: any) {
      console.error("Failed to save transfers:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save transfers",
      );
    }
  };

  const switchToCreateMode = () => {
    navigate("/inventory/movements/form?mode=create");
  };

  const handleCancel = () => {
    navigate("/inventory/movements");
  };

  const handleDialogOpen = (
    index: number,
    lineData: newMovementRequest | null,
  ) => {
    setDialogOpen(true);
    setCurDialogData(lineData);
    setCurDialogIndex(index);
  };

  const handleDialogSubmit = (data: newMovementRequest) => {
    if (curDialogIndex === -1) {
      append(data);
    } else {
      update(curDialogIndex, data);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurDialogData(null);
    setCurDialogIndex(-1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleCancel}
            variant="outlined"
          >
            Back to Inventory Movements
          </Button>
          <Typography variant="h4" component="h1">
            {formMode === "create" ? "Inventory Transfer" : ""}
          </Typography>
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToCreateMode}
              startIcon={<GenericAdd />}
            >
              Create New
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              console.log(getValues());
            }}
          >
            DEBUG PRINT
          </Button>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <Stack spacing={3}>
              <Stack sx={{ display: "column", gap: 1, width: "50%" }}>
                <Controller
                  name="movement_type"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => {
                    return (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        disabled={watchedMovements.length > 0}
                        value={value}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        options={movTypeOpts}
                        getOptionLabel={(option: AMovTypeOption) =>
                          option?.label || ""
                        }
                        isOptionEqualToValue={(
                          option: AMovTypeOption,
                          value: AMovTypeOption,
                        ) => {
                          return option.value.id === value.value.id;
                        }}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              label="Transfer Type"
                              error={!!formErrors.movement_type}
                              helperText={
                                !!formErrors.movement_type
                                  ? formErrors.movement_type?.message
                                  : "Select a Transfer Type to begin."
                              }
                            />
                          );
                        }}
                      />
                    );
                  }}
                />
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        label="Notes"
                        multiline={true}
                        rows={3}
                        fullWidth
                        error={!!formErrors.notes}
                        helperText={formErrors.notes?.message}
                      />
                    );
                  }}
                />
              </Stack>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: "center" }}>Row #</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Lot</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>From</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>To</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        Quantity
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Notes</TableCell>
                      <TableCell sx={{ textAlign: "center" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => {
                      return (
                        <MovementRow
                          key={field.id}
                          index={index}
                          remove={remove}
                          formMode={formMode}
                          xferMode={watchedMovType}
                          lineData={
                            field as newMovementRequest & { id: string }
                          }
                          handleDialogOpen={handleDialogOpen}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {!!watchedMovType && (
                <MovementDialog
                  open={dialogOpen}
                  initData={curDialogData}
                  onDialogClose={handleDialogClose}
                  onDialogSubmit={handleDialogSubmit}
                  mode={watchedMovType}
                  superUser={user?.is_superuser || false}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<GenericAdd />}
                  onClick={() => handleDialogOpen(-1, null)}
                  disabled={!watchedMovType}
                >
                  Add Line
                </Button>
              </Box>

              {/* Form Actions */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  startIcon={<BadIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : formMode === "create"
                      ? "Move Inventory"
                      : "Update Inventory Movement"}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};
